<script>
    import CRUD from './CRUD.svelte';
    import { onMount } from 'svelte';
    import { t } from 'svelte-i18n';
    import Swal from 'sweetalert2';
    import { 
        fetchCotizaCabs, 
        saveCotizaCab, 
        removeCotizaCab, 
        searchCotizaCabs, 
        subscribeToCotizaCabs, 
        unsubscribeFromCotizaCabs,
        generateCotizaNumber
    } from '../controllers/cotizaCabController.js';
    import { 
        fetchCotizaDets, 
        saveCotizaDet, 
        removeCotizaDet, 
        searchCotizaDets, 
        subscribeToCotizaDets, 
        unsubscribeFromCotizaDets,
        updateCotizaCabTotal
    } from '../controllers/cotizaDetController.js';
    import { fetchProductos } from '../controllers/productoController.js';
    import { 
        fetchAtributoCotizasByCotizaCab, 
        saveAtributoCotiza, 
        removeAtributoCotiza, 
        searchAtributoCotizas, 
        subscribeToAtributoCotizas, 
        unsubscribeFromAtributoCotizas
    } from '../controllers/atributoCotizaController.js';
    import { generateQuotationPDF } from '../lib/pdfGenerator.js';
    import { EMAIL_API_URL } from '../config.js';
    import jsPDF from 'jspdf';
    import 'jspdf-autotable';
    
    // Import PDF functions from pdfGenerator
    import { 
        addCoverPage,
        addHeader, 
        addQuotationInfo, 
        addClientInfo, 
        addDetailsTable, 
        addImages, 
        addFooter 
    } from '../lib/pdfGenerator.js';

    // Accept router props to avoid warnings
    export const location = undefined;
    export const id = undefined;


    // Configuración de las columnas para la tabla de cotizaciones
    let cotizaColumns = [
        { label: $t('quotation.fields.number'), field: 'numero', type: 'text' },
        { label: $t('quotation.fields.date'), field: 'fecha', type: 'date' },
        { label: $t('quotation.fields.client'), field: 'nombre', type: 'text' },
        { label: $t('quotation.fields.nit'), field: 'nit', type: 'text' },
        { label: $t('quotation.fields.phone'), field: 'telefono', type: 'text' },
        { label: $t('quotation.fields.email'), field: 'correo', type: 'email' },
        { label: $t('quotation.fields.total'), field: 'total', type: 'currency' }
    ];

    // Estructura del nuevo objeto CotizaCab
    let newCotizaCab = {
        numero: '000001', // Default number, will be updated by generateNumber()
        fecha: new Date().toISOString().split('T')[0],
        nit: '',
        nombre: '',
        telefono: '',
        correo: '',
        total: 0,
        atributos: true // Enable atributos tab
    };

    // Configuración de los campos para la edición de cotizaciones
    let editColumns = [
        { label: $t('quotation.fields.number'), key: 'numero', type: 'text', required: true, readonly: true },
        { label: $t('quotation.fields.date'), key: 'fecha', type: 'date', required: true },
        { label: $t('quotation.fields.nit'), key: 'nit', type: 'text', required: true },
        { label: $t('quotation.fields.clientName'), key: 'nombre', type: 'text', required: true },
        { label: $t('quotation.fields.phone'), key: 'telefono', type: 'text', required: false },
        { label: $t('quotation.fields.emailAddress'), key: 'correo', type: 'email', required: false },
        { label: $t('quotation.fields.total'), key: 'total', type: 'number', required: true, readonly: true }
    ];

    // Configuración para los detalles de cotización
    let cotizaDetColumns = [
        { 
            label: $t('quotation.fields.product'), 
            field: 'producto', 
            type: 'relation', 
            relationField: 'nombre',
            format: (value, row) => {
                const producto = row.expand?.producto;
                return producto ? `${producto.sku || ''} - ${producto.nombre || ''}` : value;
            }
        },
        { label: $t('quotation.fields.quantity'), field: 'cantidad', type: 'number' },
        { label: $t('quotation.fields.unitPrice'), field: 'precio', type: 'currency' },
        { label: $t('quotation.fields.total'), field: 'total', type: 'currency' }
    ];

    let cotizaDetEditColumns = [
        { 
            label: $t('quotation.fields.product'), 
            key: 'producto', 
            type: 'select', 
            required: true, 
            options: [] 
        },
        { label: $t('quotation.fields.quantity'), key: 'cantidad', type: 'number', required: true, min: 1 },
        { label: $t('quotation.fields.unitPrice'), key: 'precio', type: 'number', required: true, min: 0, step: 0.01 },
        { label: $t('quotation.fields.total'), key: 'total', type: 'number', required: true, readonly: true }
    ];

    // Función para cargar productos
    async function loadProductos() {
        try {
            const response = await fetchProductos(1, 1000);
            if (response && response.producto) {
                const productoOptions = response.producto.map(producto => ({
                    value: producto.id,
                    label: producto.nombre
                }));
                
                // Update the edit columns with product options
                cotizaDetEditColumns = cotizaDetEditColumns.map(col => {
                    if (col.key === 'producto') {
                        return { ...col, options: productoOptions };
                    }
                    return col;
                });
            }
        } catch (error) {
            console.error('Error loading productos:', error);
        }
    }

    // Configuración para atributos de cotización
    let atributoCotizaColumns = [
        { 
            label: $t('quotation.fields.attribute'), 
            field: 'atributo', 
            type: 'relation', 
            relationField: 'descrip',
            format: (value, row) => {
                const atributo = row.expand?.atributo;
                return atributo ? `${atributo.descrip || 'Sin descripción'}` : value;
            }
        },
        { 
            label: $t('quotation.fields.value'), 
            field: 'valor', 
            type: 'text'
        }
    ];

    let atributoOptions = [];
    
    $: atributoCotizaEditColumns = [
        { 
            label: $t('quotation.fields.attribute'), 
            key: 'atributo', 
            type: 'select', 
            required: true, 
            options: atributoOptions 
        },
        { 
            label: $t('quotation.fields.value'), 
            key: 'valor', 
            type: 'text',
            required: false,
            dynamicType: true // Flag to indicate this field should be dynamic
        }
    ];

    // Función para cargar atributos (solo los de tipo "cotiza")
    async function loadAtributos() {
        try {
            // Fetch atributos with filter for tipo = "cotiza"
            const response = await fetchAtributosByType('cotiza', 1, 1000);
            if (response && response.atributo) {
                atributoOptions = response.atributo.map(atributo => ({
                    value: atributo.id,
                    label: atributo.descrip || 'Sin descripción',
                    atributo: atributo // Include the full atributo object for dynamic field types
                }));
            }
        } catch (error) {
            console.error('Error loading atributos:', error);
        }
    }

    // Función para obtener atributos por tipo
    async function fetchAtributosByType(tipo, page = 1, perPage = 100) {
        try {
            const pb = (await import('../lib/pocketbase')).default;
            const response = await pb.collection('atributo').getList(page, perPage, {
                filter: `tipo = "${tipo}"`,
                sort: 'descrip',
                $autoCancel: false
            });
            return {
                atributo: response.items,
                totalRecords: response.totalItems,
                currentPage: response.page,
            };
        } catch (error) {
            console.error("Error fetching atributos by type:", error);
            return null;
        }
    }

    // Función para generar número de cotización automático
    async function generateNumber() {
        try {
            const numero = await generateCotizaNumber();
            newCotizaCab.numero = numero;
        } catch (error) {
            console.error('Error generating cotiza number:', error);
        }
    }

    // Función para manejar cuando se guarda un detalle
    async function handleDetailSaved() {
        // Update the total of the quotation
        // This will be called when a detail is saved
    }

    // Función para manejar cuando se elimina un detalle
    async function handleDetailRemoved() {
        // Update the total of the quotation
        // This will be called when a detail is removed
    }

    // PDF Generation functionality
    let selectedQuotation = null;
    let selectedQuotationDetails = [];
    let isGeneratingPDF = false;
    let isSendingEmail = false;

    // Function to handle PDF generation
    async function handleGeneratePDF(quotation) {
        try {
            isGeneratingPDF = true;
            console.log('Starting PDF generation for quotation:', quotation.id);
            
            // Fetch quotation details - fix parameter order: cotizaCabId, page, perPage
            const details = await fetchCotizaDets(quotation.id, 1, 1000);
            const quotationDetails = details?.cotiza_det || [];
            console.log('Quotation details found:', quotationDetails.length);
            
            // Fetch actual gallery images from cotiza_gallery collection
            const galleryImages = await fetchGalleryImages(quotation.id);
            console.log('Gallery images for PDF:', galleryImages);
            
            // Generate PDF
            await generateQuotationPDF(quotation, quotationDetails, galleryImages);
            console.log('PDF generation completed successfully');
            
        } catch (error) {
            console.error('Error generating PDF:', error);
            
            // Show user-friendly error message
            let errorMessage = 'Error al generar el PDF';
            if (error.message.includes('No quotation data')) {
                errorMessage = 'No se encontraron datos de cotización válidos';
            } else if (error.message.includes('Invalid arguments')) {
                errorMessage = 'Error en el formato del documento';
            } else if (error.message.includes('400')) {
                errorMessage = 'Error al cargar los detalles de la cotización';
            }
            
            alert(errorMessage + ': ' + error.message);
        } finally {
            isGeneratingPDF = false;
        }
    }

    // Function to handle email sending
    async function handleSendEmail(quotation) {
        try {
            isSendingEmail = true;
            console.log('Starting email sending for quotation:', quotation.id);
            console.log('Quotation object:', quotation);
            console.log('Available fields:', Object.keys(quotation));
            console.log('Email field (correo):', quotation.correo);
            console.log('All field values:', Object.entries(quotation));
            
            // Check if quotation has client email (field name is 'correo' in cotiza_cab)
            if (!quotation.correo || quotation.correo.trim() === '') {
                Swal.fire({
                    title: 'Email Requerido',
                    text: 'No se ha especificado un email para el cliente en esta cotización.',
                    icon: 'warning',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#dc2626',
                    showConfirmButton: true,
                    customClass: {
                        popup: 'swal2-popup-custom',
                        title: 'swal2-title-custom',
                        content: 'swal2-content-custom'
                    }
                });
                return;
            }
            
            // Generate PDF first
            const details = await fetchCotizaDets(quotation.id, 1, 1000);
            const quotationDetails = details?.cotiza_det || [];
            const galleryImages = await fetchGalleryImages(quotation.id);
            
            // Generate PDF and get the blob
            const pdfBlob = await generateQuotationPDFBlob(quotation, quotationDetails, galleryImages);
            
            // Convert blob to base64
            const pdfBase64 = await blobToBase64(pdfBlob);
            
            // Send email with PDF attachment
            try {
                const requestBody = {
                    quotation: quotation,
                    clientEmail: quotation.correo,
                    pdfBase64: pdfBase64 || ''
                };
                
                console.log('Sending email request to:', `${EMAIL_API_URL}`);
                console.log('Request body:', { ...requestBody, pdfBase64: pdfBase64 ? `[${pdfBase64.length} chars]` : 'empty' });
                
                const response = await fetch(`${EMAIL_API_URL}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody)
                });
                
                // Check content type before parsing JSON
                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    const errorText = await response.text().catch(() => 'Unknown error');
                    console.error('Email API returned non-JSON response (HTML):', errorText.substring(0, 300));
                    throw new Error(`Email service returned HTML instead of JSON. This usually means nginx can't reach the email server on localhost:3008. Status: ${response.status}`);
                }
                
                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Unknown error');
                    console.error('Email API error response:', errorText);
                    throw new Error(`Email service error: ${response.status} - ${errorText}`);
                }
                
                const result = await response.json();
                console.log('Email sent successfully:', result);
                
            } catch (emailError) {
                console.error('Email service not available:', emailError);
                console.error('Email API URL:', `${EMAIL_API_URL}`);
                
                // Show error to user
                Swal.fire({
                    icon: 'warning',
                    title: 'Email no enviado',
                    text: 'El PDF se generó correctamente, pero no se pudo enviar el email. El servicio de email no está disponible en este momento.',
                    confirmButtonText: 'OK'
                });
                return;
            }
            
            // Show success notification
            Swal.fire({
                title: '¡Email Enviado!',
                text: `La cotización ha sido enviada exitosamente a ${quotation.correo}`,
                icon: 'success',
                confirmButtonText: 'Perfecto',
                confirmButtonColor: '#dc2626',
                timer: 4000,
                timerProgressBar: true,
                showConfirmButton: true,
                customClass: {
                    popup: 'swal2-popup-custom',
                    title: 'swal2-title-custom',
                    content: 'swal2-content-custom'
                }
            });
            
        } catch (error) {
            console.error('Error sending email:', error);
            // Show beautiful error notification
            Swal.fire({
                title: 'Error al Enviar Email',
                text: error.message || 'Ocurrió un error inesperado al enviar el email',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#dc2626',
                showConfirmButton: true,
                customClass: {
                    popup: 'swal2-popup-custom',
                    title: 'swal2-title-custom',
                    content: 'swal2-content-custom'
                }
            });
        } finally {
            isSendingEmail = false;
        }
    }

    // Helper function to generate PDF and return blob
    async function generateQuotationPDFBlob(quotation, details = [], images = []) {
        try {
            // Validate inputs
            if (!quotation) {
                throw new Error('No quotation data provided');
            }

            // Create new PDF document
            const doc = new jsPDF('p', 'mm', 'a4');
            
            // Set up colors (Nonosky palette: black background with red accents)
            const colors = {
                primary: '#DC2626', // Red-600
                secondary: '#7F1D1D', // Red-800
                background: '#000000', // Black
                text: '#FFFFFF', // White
                textSecondary: '#D1D5DB' // Gray-300
            };

            // Add cover page first
            await addCoverPage(doc, colors);
            
            // Add new page for content
            doc.addPage();

            // Add background
            doc.setFillColor(colors.background);
            doc.rect(0, 0, 210, 297, 'F');

            // Add header with company branding
            await addHeader(doc, colors);
            
            // Add quotation information
            addQuotationInfo(doc, quotation, colors, details);
            
            // Add client information
            addClientInfo(doc, quotation, colors);
            
            // Add details table
            addDetailsTable(doc, details, colors);
            
            // Add images if available
            if (images && images.length > 0) {
                await addImages(doc, images, colors);
            }
            
            // Add footer
            addFooter(doc, colors);
            
            // Return PDF as blob
            return doc.output('blob');
            
        } catch (error) {
            console.error('Error generating PDF blob:', error);
            throw error;
        }
    }

    // Helper function to convert blob to base64
    function blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result.split(',')[1]; // Remove data:application/pdf;base64, prefix
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    // Function to fetch gallery images from cotiza_gallery collection
    async function fetchGalleryImages(cotizaCabId) {
        try {
            const pb = (await import('../lib/pocketbase')).default;
            console.log('🖼️ Fetching images for cotiza_cab:', cotizaCabId);
            
            const allImages = [];
            
            // 1. Get images from cotiza_gallery (quotation-specific images)
            try {
                console.log('📸 Fetching images from cotiza_gallery...');
                const cotizaGalleryResponse = await pb.collection('cotiza_gallery').getList(1, 10, {
                    filter: `cotiza_cab="${cotizaCabId}"`,
                    sort: '-created'
                });
                
                if (cotizaGalleryResponse.items && cotizaGalleryResponse.items.length > 0) {
                    const cotizaImages = cotizaGalleryResponse.items.map(item => {
                        const baseUrl = pb.baseUrl;
                        const fullUrl = `${baseUrl}/api/files/${item.collectionId}/${item.id}/${item.url}`;
                        console.log('✅ Constructed cotiza_gallery image URL:', fullUrl);
                        return fullUrl;
                    });
                    allImages.push(...cotizaImages);
                    console.log(`✅ Found ${cotizaImages.length} images from cotiza_gallery`);
                } else {
                    console.log('⚠️ No images found in cotiza_gallery');
                }
            } catch (cotizaError) {
                console.error('❌ Error fetching cotiza_gallery images:', cotizaError);
            }
            
            // 2. Get images from producto_gallery (component images)
            try {
                console.log('🔧 Fetching component images from producto_gallery...');
                
                // Get quotation details to find the products
                const details = await fetchCotizaDets(cotizaCabId, 1, 1000);
                const quotationDetails = details?.cotiza_det || [];
                console.log('📦 Quotation details for component images:', quotationDetails.length);
                
                if (quotationDetails.length > 0) {
                    // Get all product IDs from the quotation details
                    const productIds = quotationDetails.map(detail => detail.producto).filter(Boolean);
                    console.log('🔍 Product IDs from quotation:', productIds);
                    
                    if (productIds.length > 0) {
                        // Fetch images for each product from producto_gallery
                        for (const productId of productIds) {
                            try {
                                console.log('🖼️ Fetching component images for product:', productId);
                                const response = await pb.collection('producto_gallery').getList(1, 10, {
                                    filter: `producto="${productId}"`,
                                    sort: '-created'
                                });
                                
                                if (response.items && response.items.length > 0) {
                                    const productImages = response.items.map(item => {
                                        const baseUrl = pb.baseUrl;
                                        const fullUrl = `${baseUrl}/api/files/${item.collectionId}/${item.id}/${item.url}`;
                                        console.log('✅ Constructed product image URL:', fullUrl);
                                        return fullUrl;
                                    });
                                    allImages.push(...productImages);
                                    console.log(`✅ Found ${productImages.length} component images for product ${productId}`);
                                } else {
                                    console.log(`⚠️ No component images found for product ${productId}`);
                                }
                            } catch (productError) {
                                console.error(`❌ Error fetching component images for product ${productId}:`, productError);
                            }
                        }
                    } else {
                        console.log('❌ No product IDs found in quotation details');
                    }
                } else {
                    console.log('❌ No quotation details found for component images');
                }
            } catch (productoError) {
                console.error('❌ Error fetching producto_gallery images:', productoError);
            }
            
            console.log('🎯 Total images found (cotiza_gallery + producto_gallery):', allImages.length);
            console.log('📸 All image URLs:', allImages);
            return allImages;
            
        } catch (error) {
            console.error('❌ Error fetching gallery images:', error);
            return [];
        }
    }

    // Cargar número automático, productos y atributos al montar el componente
    onMount(() => {
        generateNumber();
        loadProductos();
        loadAtributos();
    });
</script>

<CRUD 
    title="Cotizaciones"
    entityName="cotiza_cab"
    newEntity={newCotizaCab}
    columns={cotizaColumns}
    fetchEntities={fetchCotizaCabs}
    saveEntity={saveCotizaCab}
    removeEntity={removeCotizaCab}
    searchEntities={searchCotizaCabs}
    subscribeToChanges={subscribeToCotizaCabs}
    unsubscribeFromChanges={unsubscribeFromCotizaCabs}
    editColumns={editColumns}
    onNewEntity={generateNumber}
    generateQuotationPDF={handleGeneratePDF}
    handleSendEmail={handleSendEmail}
    isSendingEmail={isSendingEmail}
    enableDetails={true}
    detailConfig={{ 
        collection: 'cotiza_det', 
        relationField: 'cotiza_cab', 
        tabLabel: $t('quotation.tabs.itemDetails'),
        columns: cotizaDetColumns,
        editColumns: cotizaDetEditColumns,
        fetchDetails: fetchCotizaDets,
        saveDetail: saveCotizaDet,
        removeDetail: removeCotizaDet,
        searchDetails: searchCotizaDets,
        subscribeToDetails: subscribeToCotizaDets,
        unsubscribeFromDetails: unsubscribeFromCotizaDets,
        onDetailSaved: handleDetailSaved,
        onDetailRemoved: handleDetailRemoved
    }}
    atributosConfig={{
        collection: 'atributo_cotiza',
        relationField: 'cotiza_cab',
        columns: atributoCotizaColumns,
        editColumns: atributoCotizaEditColumns,
        fetchAtributos: fetchAtributoCotizasByCotizaCab,
        saveAtributo: saveAtributoCotiza,
        removeAtributo: removeAtributoCotiza,
        searchAtributos: searchAtributoCotizas,
        subscribeToAtributos: subscribeToAtributoCotizas,
        unsubscribeFromAtributos: unsubscribeFromAtributoCotizas
    }}
    enableGallery={true}
    galleryConfig={{
        collection: 'cotiza_gallery',
        relationField: 'cotiza_cab',
        tabLabel: $t('quotation.tabs.gallery'),
        urlField: 'url',
        fileField: 'url' // The 'url' field is actually a file field
    }}
/>
